import envConfig from '@hulu/env-config';
import { useLocation } from '@hulu/react-router-dom';
import { getJwtTokenPayload, User } from 'mission-control-common-components';
import { useEffect, useRef } from 'react';

import { LOCATION_BEFORE_LOGIN } from '../../common/constants';
import { getCognitoDetails } from '../../utils/cognitoDetails';
import { useFeatureFlags } from '../useFeatureFlags';

type TokenPayloadWithAudience = {
  aud?: string | string[];
};

export type UseLogoutOnCognitoPoolFlagChangeProps = {
  isAuthEnabled: boolean;
};

const normalizeAudiences = (aud?: string | string[]): string[] => {
  if (!aud) {
    return [];
  }

  return Array.isArray(aud) ? aud : [aud];
};

const tokenMatchesConfiguredPool = (audiences: string[], clientId: string | undefined): boolean =>
  Boolean(clientId && audiences.includes(clientId));

/**
 * Re-login only when the LaunchDarkly flag moves forward (old token + new pool flag).
 * A stale `newCognitoPoolEnabled: false` while the session token is from the new pool must not
 * force logout on plugin navigation — that produced redirects to the old Cognito client on mc-dev.
 */
export const useLogoutOnCognitoPoolFlagChange = ({ isAuthEnabled }: UseLogoutOnCognitoPoolFlagChangeProps): void => {
  const currentLocation = useLocation();
  const { newCognitoPoolEnabled } = useFeatureFlags();
  const logoutInitiatedRef = useRef(false);

  useEffect(() => {
    if (
      !isAuthEnabled ||
      newCognitoPoolEnabled === undefined ||
      newCognitoPoolEnabled === null ||
      logoutInitiatedRef.current
    ) {
      return;
    }

    const { idToken } = User.getUserInfo();

    if (!idToken) {
      return;
    }

    const tokenPayload = getJwtTokenPayload(idToken) as TokenPayloadWithAudience | null;
    const audiences = normalizeAudiences(tokenPayload?.aud);

    if (!audiences.length) {
      return;
    }

    const matchesNewPool = tokenMatchesConfiguredPool(audiences, envConfig.REACT_APP_COGNITO_CLIENT_ID_NEW);
    const matchesOldPool = tokenMatchesConfiguredPool(audiences, envConfig.REACT_APP_COGNITO_CLIENT_ID);

    if (newCognitoPoolEnabled && matchesOldPool && !matchesNewPool) {
      logoutInitiatedRef.current = true;
      localStorage.setItem(LOCATION_BEFORE_LOGIN, JSON.stringify(currentLocation));
      User.logout(getCognitoDetails(true));
    }
  }, [currentLocation, isAuthEnabled, newCognitoPoolEnabled]);
};
